'use client';

import { Check, ChevronRight, X } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * One line of "what the assistant did".
 *
 * `summary` is rendered as PLAIN TEXT, never markdown. Live it is the tool's
 * title ("List hosts"); replayed it is up to 200 characters of the raw tool
 * result — hostnames, paths and email subjects typed by tenants and by
 * anonymous visitors. That is data, and data does not get a renderer that can
 * follow instructions in it: no markdown parser, no `dangerouslySetInnerHTML`,
 * just a text node inside a `<pre>`-style block so whitespace in the raw JSON
 * stays legible.
 *
 * `ok: false` is routine and self-correcting (the model retried and the turn
 * continues) — it gets a muted inline note, never a toast and never anything
 * styled as an error/alert.
 *
 * A native `<details>` disclosure, not a shadcn/Radix primitive: there is no
 * `Collapsible` component in this repo's `components/ui`, and pulling in
 * `@radix-ui/react-collapsible` for a plain expand/collapse row — no animation
 * requirement, no need for controlled state shared with anything else — would
 * add a dependency to reinvent what `<details>` already gives for free
 * (keyboard toggling, no JS needed to render collapsed).
 */
export function ToolActivityBlock({
  tool,
  ok,
  summary,
}: {
  tool: string;
  ok: boolean;
  summary: string;
}) {
  const label = tool.replace(/_/g, ' ') || 'Working';

  return (
    <details className="group rounded-md border bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
      <summary className="flex cursor-pointer list-none items-center gap-1.5">
        <ChevronRight className="size-3 transition-transform group-open:rotate-90" />
        {ok ? (
          <Check className="size-3 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <X className="size-3 text-muted-foreground" />
        )}
        <span className="truncate">{label}</span>
        {!ok && <span className="shrink-0 italic">— couldn&rsquo;t do that</span>}
      </summary>
      <p className={cn('mt-1 break-words whitespace-pre-wrap font-mono text-[11px]', 'text-muted-foreground')}>
        {summary}
      </p>
    </details>
  );
}
